import React, { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { marketingApi, readFileAsDataUrl } from '../marketingApi';

interface Props {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
}

const MAX_IMAGE_MB = 5;

/* Tailwind's preflight strips heading/list styles, so the editing surface
   gets its own minimal typography that roughly matches the email. */
const editorCss = `
.email-editor .ProseMirror { min-height: 320px; padding: 16px 20px; outline: none; color: #1f2937; font-size: 16px; line-height: 1.6; }
.email-editor .ProseMirror p { margin: 0 0 14px; }
.email-editor .ProseMirror h1 { font-size: 26px; font-weight: 700; margin: 0 0 14px; line-height: 1.3; }
.email-editor .ProseMirror h2 { font-size: 21px; font-weight: 700; margin: 20px 0 10px; }
.email-editor .ProseMirror h3 { font-size: 18px; font-weight: 600; margin: 18px 0 8px; }
.email-editor .ProseMirror ul { list-style: disc; padding-left: 24px; margin: 0 0 14px; }
.email-editor .ProseMirror ol { list-style: decimal; padding-left: 24px; margin: 0 0 14px; }
.email-editor .ProseMirror li p { margin: 0 0 4px; }
.email-editor .ProseMirror blockquote { border-left: 4px solid #2563eb; background: #eff6ff; padding: 8px 16px; margin: 0 0 14px; }
.email-editor .ProseMirror hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
.email-editor .ProseMirror a { color: #2563eb; text-decoration: underline; }
.email-editor .ProseMirror img { max-width: 100%; height: auto; border-radius: 8px; margin: 0 auto 14px; display: block; }
.email-editor .ProseMirror img.ProseMirror-selectednode { outline: 3px solid #93c5fd; }
.email-editor .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #9ca3af; float: left; height: 0; pointer-events: none; }
`;

const ToolButton: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onMouseDown={(e) => e.preventDefault()} // keep editor selection
    onClick={onClick}
    disabled={disabled}
    className={`min-w-[32px] h-8 px-2 rounded text-sm font-medium transition-colors disabled:opacity-40 ${
      active ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-5 bg-gray-200 mx-1" />;

function promptLink(editor: Editor) {
  const prev = editor.getAttributes('link').href as string | undefined;
  const url = window.prompt('Link URL (https://… or mailto:…). Leave empty to remove the link.', prev || 'https://');
  if (url === null) return;
  const trimmed = url.trim();
  if (!trimmed || trimmed === 'https://') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }
  if (!/^(https?:\/\/|mailto:)/i.test(trimmed)) {
    window.alert('Links must start with https://, http:// or mailto:');
    return;
  }
  editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
}

const EmailBodyEditor: React.FC<Props> = ({ value, onChange, readOnly = false }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        code: false,
        codeBlock: false,
      }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, protocols: ['mailto'] }),
      Image.configure({ inline: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Write your email here…' }),
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? '' : ed.getHTML()),
  });

  // Load content when switching campaigns (not on every keystroke)
  useEffect(() => {
    if (editor && value !== editor.getHTML() && !(value === '' && editor.isEmpty)) {
      editor.commands.setContent(value, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  const handleImage = async (file: File) => {
    if (!editor) return;
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      window.alert(`Image must be under ${MAX_IMAGE_MB} MB.`);
      return;
    }
    const alt = window.prompt('Describe the image (shown if the image is blocked, and read by screen readers):', '');
    if (alt === null) return;
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const { url } = await marketingApi.uploadImage(dataUrl);
      editor.chain().focus().setImage({ src: url, alt: alt.trim() }).run();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!editor) return null;
  const e = editor;

  return (
    <div className="email-editor border border-gray-300 rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
      <style>{editorCss}</style>

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <ToolButton title="Heading 1" active={e.isActive('heading', { level: 1 })} onClick={() => e.chain().focus().toggleHeading({ level: 1 }).run()}>H1</ToolButton>
          <ToolButton title="Heading 2" active={e.isActive('heading', { level: 2 })} onClick={() => e.chain().focus().toggleHeading({ level: 2 }).run()}>H2</ToolButton>
          <ToolButton title="Heading 3" active={e.isActive('heading', { level: 3 })} onClick={() => e.chain().focus().toggleHeading({ level: 3 }).run()}>H3</ToolButton>
          <Divider />
          <ToolButton title="Bold" active={e.isActive('bold')} onClick={() => e.chain().focus().toggleBold().run()}><b>B</b></ToolButton>
          <ToolButton title="Italic" active={e.isActive('italic')} onClick={() => e.chain().focus().toggleItalic().run()}><i>I</i></ToolButton>
          <ToolButton title="Underline" active={e.isActive('underline')} onClick={() => e.chain().focus().toggleUnderline().run()}><u>U</u></ToolButton>
          <ToolButton title="Strikethrough" active={e.isActive('strike')} onClick={() => e.chain().focus().toggleStrike().run()}><s>S</s></ToolButton>
          <Divider />
          <ToolButton title="Bullet list" active={e.isActive('bulletList')} onClick={() => e.chain().focus().toggleBulletList().run()}>• List</ToolButton>
          <ToolButton title="Numbered list" active={e.isActive('orderedList')} onClick={() => e.chain().focus().toggleOrderedList().run()}>1. List</ToolButton>
          <ToolButton title="Quote / highlight box" active={e.isActive('blockquote')} onClick={() => e.chain().focus().toggleBlockquote().run()}>❝</ToolButton>
          <ToolButton title="Divider line" onClick={() => e.chain().focus().setHorizontalRule().run()}>―</ToolButton>
          <Divider />
          <ToolButton title="Align left" active={e.isActive({ textAlign: 'left' })} onClick={() => e.chain().focus().setTextAlign('left').run()}><svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24"><path d="M4 6h16M4 10h10M4 14h16M4 18h10" /></svg></ToolButton>
          <ToolButton title="Align center" active={e.isActive({ textAlign: 'center' })} onClick={() => e.chain().focus().setTextAlign('center').run()}><svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24"><path d="M4 6h16M7 10h10M4 14h16M7 18h10" /></svg></ToolButton>
          <ToolButton title="Align right" active={e.isActive({ textAlign: 'right' })} onClick={() => e.chain().focus().setTextAlign('right').run()}><svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24"><path d="M4 6h16M10 10h10M4 14h16M10 18h10" /></svg></ToolButton>
          <Divider />
          <ToolButton title="Link" active={e.isActive('link')} onClick={() => promptLink(e)}>🔗 Link</ToolButton>
          <ToolButton title="Insert image" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? 'Uploading…' : '🖼 Image'}
          </ToolButton>
          <Divider />
          <ToolButton title="Undo" disabled={!e.can().undo()} onClick={() => e.chain().focus().undo().run()}>↶</ToolButton>
          <ToolButton title="Redo" disabled={!e.can().redo()} onClick={() => e.chain().focus().redo().run()}>↷</ToolButton>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden"
            onChange={(ev) => {
              const f = ev.target.files?.[0];
              ev.target.value = '';
              if (f) handleImage(f);
            }}
          />
        </div>
      )}

      <EditorContent editor={editor} />
    </div>
  );
};

export default EmailBodyEditor;
